// API configuration and service functions for the quiz admin app

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_URL || "https://bigquiz.app/api";
const API_URL = process.env.NEXT_PUBLIC_API_URL || "https://bigquiz.app/api";

export const resolveApiMediaUrl = (url?: string | null) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  const publicUrl = url.replace(
    /^\/api\/questions\/(\d+)\/image$/,
    "/api/public/questions/$1/image",
  );
  const apiOrigin = API_URL.replace(/\/api\/?$/, "");
  return `${apiOrigin}${publicUrl.startsWith("/") ? publicUrl : `/${publicUrl}`}`;
};

export const categoryPhotoUrl = (categoryId: number, version?: number) =>
  `${API_URL}/categories/photo/${categoryId}${version ? `?v=${version}` : ""}`;

// Types based on the OpenAPI schema
export interface LanguageCode {
  code: string;
  name: string;
  flag?: string;
}

export interface LanguageOut {
  id: number;
  code: string;
  name: string;
  flag: string;
}
export interface CategoryTranslationBase {
  language: LanguageCode;
  name: string;
}

export interface CategoryTranslationOut extends CategoryTranslationBase {
  language_id: number;
}

export interface CategoryCreate {
  translations: CategoryTranslationBase[];
}

// export interface CategoryOut {
//   id: number
//   draft: boolean
//   created_at: string
//   translations: CategoryTranslationOut[]
//   questions_count: number
// }
export interface CategoryOut {
  id: number;
  status: CategoryStatus; // Added status
  created_at: string;
  updated_at: string; // Added updated_at
  translations: CategoryTranslationOut[];
  questions_count: number;
  draft: boolean;
  tags: string[]; // Added tags
}

export const getCategoryDisplayName = (
  category?: Pick<CategoryOut, "id" | "translations"> | null,
) =>
  category?.translations?.find((translation) => translation.name?.trim())?.name ||
  (category ? `Category #${category.id}` : "Unknown");

export interface QuestionTranslationBase {
  language: LanguageCode;
  question_text: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  correct_option: string;
}

export interface QuestionTranslationOut extends QuestionTranslationBase {
  language_id: number;
}

export interface QuestionCreate {
  category_id: number;
  difficulty?: string;
  image_url?: string | null;
  translations: QuestionTranslationBase[];
}

export interface QuestionOut {
  id: number;
  category_id: number;
  difficulty: string;
  image_url?: string | null;
  translations: QuestionTranslationOut[];
  translations_count: number;
}

export interface RagSimilarQuestion {
  question_id: number;
  category_id: number;
  language_code: string;
  question_text: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  correct_option: string;
  similarity: number;
}

export interface RagGeneratedQuestion extends QuestionTranslationBase {
  duplicate_score: number;
  duplicate_question_id?: number | null;
  safe_to_create: boolean;
  reason: string;
}

export interface RagGenerateRequest {
  category_id: number;
  category_name?: string;
  language_code: string;
  topic: string;
  count: number;
  difficulty: string;
  similarity_threshold?: number;
}

export interface RagGenerateResponse {
  category_id: number;
  language_code: string;
  topic: string;
  generated: RagGeneratedQuestion[];
  retrieved: RagSimilarQuestion[];
  blocked_duplicates: number;
}
// New User Types from OpenAPI
export type UserRole = "admin" | "moderator" | "user";

export interface UserBase {
  id: number;
  username: string;
  email: string;
  status: boolean;
  xp: number;
  level: number;
  coins: number;
  energy: number;
  role: UserRole;
  created_at: string;
  google_id: string | null;
  os?: string | null;
}

export interface UserUpdate {
  username?: string | null;
  email?: string | null;
  password?: string | null; // Password update is optional
  status?: boolean | null;
  xp?: number | null;
  level?: number | null;
  coins?: number | null;
  energy?: number | null;
  role?: UserRole | null;
}

export interface AdminStatisticsPoint {
  label: string;
  count: number;
}

export interface AdminRecentUser {
  id: number;
  username: string;
  email: string;
  role: UserRole;
  status: boolean;
  is_anonymous: boolean;
  os?: string | null;
  created_at: string;
}

export interface AdminRecentDuelPlayer {
  user_id: number;
  username: string;
  score: number;
  rank: number;
}

export interface AdminRecentDuel {
  room_code: string;
  category_id: number;
  language_code: string;
  status: string;
  played_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  total_questions: number;
  players: AdminRecentDuelPlayer[];
}

export interface AdminStatistics {
  totals: {
    users: number;
    active_users: number;
    anonymous_users: number;
    connected_users: number;
    signups_today: number;
    sessions: number;
    completed_sessions: number;
    sessions_today: number;
  };
  duels: {
    total: number;
    completed: number;
    active: number;
    today: number;
    average_score: number;
  };
  recent_users: AdminRecentUser[];
  recent_duels: AdminRecentDuel[];
  users_by_day: AdminStatisticsPoint[];
  users_by_hour: AdminStatisticsPoint[];
  sessions_by_day: AdminStatisticsPoint[];
  duels_by_day: AdminStatisticsPoint[];
}

export interface PlatformAppVersionConfig {
  minimum_version: string;
  latest_version: string;
  store_url: string;
}

export interface AppVersionConfig {
  ios: PlatformAppVersionConfig;
  android: PlatformAppVersionConfig;
}
export interface LoginRequest {
  username: string;
  password: string;
  grant_type?: string;
  scope?: string;
  client_id?: string;
  client_secret?: string;
}

export interface Token {
  access_token: string;
  token_type?: string;
}

export type CategoryStatus = "active" | "draft" | "review" | "archived";
// API Error handling
export class APIError extends Error {
  constructor(
    message: string,
    public status: number,
    public response?: any,
  ) {
    super(message);
    this.name = "APIError";
  }
}
export interface CategoryCreate {
  translations: CategoryTranslationBase[];
  status?: CategoryStatus; // Added status for creation
  draft?: boolean;
  tags?: string[]; // Added tags for creation
}
export const getCategories = async (
  lg: string = "en",
): Promise<CategoryOut[]> => {
  const res = await fetch(`${API_URL}/categories/?lg=${lg}`, {
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error("Erreur lors de la récupération des categories");
  }
  return res.json();
};

export const getAdminCategories = async (
  lg: string = "en",
): Promise<CategoryOut[]> => {
  const res = await fetch(`${API_URL}/categories/admin?lg=${lg}`, {
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error("Erreur lors de la récupération des categories admin");
  }
  return res.json();
};

export const genQuestion = async (
  category: string,
): Promise<QuestionTranslationBase> => {
  const res = await fetch(`${API_URL}/translate/gen?category=${category}`, {
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error("Erreur lors de la récupération des languages");
  }
  return res.json();
};
export const getLanguages = async (): Promise<LanguageOut[]> => {
  const res = await fetch(`${API_URL}/languages/`, {
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error("Erreur lors de la récupération des languages");
  }
  return res.json();
};

export const deleteQuestion = async (questionsId: number): Promise<boolean> => {
  const res = await fetch(`${API_URL}/questions/?question_id=${questionsId}`, {
    method: "DELETE",
    // headers:{ "Content-Type": "application/json"},
    credentials: "include",
    // body: JSON.stringify(questions)
  });
  if (!res.ok) {
    throw new Error("Erreur lors de la récupération des languages");
  }
  return res.json();
};
export const addQuestion = async (
  questions: QuestionCreate,
): Promise<QuestionOut> => {
  const res = await fetch(`${API_URL}/questions/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(questions),
  });
  if (!res.ok) {
    throw new Error("Erreur lors de la récupération des languages");
  }
  return res.json();
};

export const uploadQuestionImage = async (
  questionId: number,
  file: File,
): Promise<QuestionOut> => {
  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(`${API_URL}/questions/${questionId}/upload-image`, {
    method: "POST",
    credentials: "include",
    body: formData,
  });
  if (!res.ok) {
    throw new Error("Erreur lors de l'upload de l'image");
  }
  return res.json();
};

export const updateQuestionImageUrl = async (
  questionId: number,
  imageUrl: string | null,
): Promise<QuestionOut> => {
  const query = imageUrl ? `?image_url=${encodeURIComponent(imageUrl)}` : "";
  const res = await fetch(`${API_URL}/questions/${questionId}/image-url${query}`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error("Erreur lors de la mise à jour de l'image");
  }
  return res.json();
};

export const getNextQuestion = async (
  questionId: number,
): Promise<QuestionOut | null> => {
  const res = await fetch(`${API_URL}/questions/${questionId}/next`, {
    method: "GET",
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error("Erreur lors de la récupération de la question suivante");
  }
  return res.json();
};

export const uploadQuestionsCsv = async ({
  categoryId,
  languageCode,
  file,
}: {
  categoryId: number;
  languageCode: string;
  file: File;
}): Promise<{ status: string; count: number }> => {
  const formData = new FormData();
  formData.append("category_id", String(categoryId));
  formData.append("language_code", languageCode);
  formData.append("file", file);

  const res = await fetch(`${API_URL}/questions/upload-questions-csv`, {
    method: "POST",
    credentials: "include",
    body: formData,
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(
      typeof errorData.detail === "string"
        ? errorData.detail
        : "Erreur lors de l'import CSV",
    );
  }

  return res.json();
};

export const getQuestions = async (
  offset: number = 0,
  limit: number = 20,
  categoryId: number = 1,
  lg: string = "en",
): Promise<QuestionOut[]> => {
  const res = await fetch(
    `${API_URL}/questions/?limit=${limit}&offset=${offset}&category_id=${categoryId}`,
    {
      method: "GET",
      // headers:{ "Content-Type": "application/json"},
      credentials: "include",
      // body: JSON.stringify(questions)
    },
  );
  if (!res.ok) {
    throw new Error("Erreur lors de la récupération des languages");
  }
  return res.json();
};

// export const translate = async (lg:string, question: QuestionTranslationBase): Promise<QuestionTranslationBase> => {
//   const res = await fetch(`${API_URL}/translate/?language=${lg}`, {
//     method: "POST",
//     headers:{ "Content-Type": "application/json"},
//     credentials: 'include',
//     body: JSON.stringify(question)
//   })
//   if (!res.ok) {
//     throw new Error("Erreur lors de la récupération des languages")
//   }
//   return res.json()
// }

export const translate = async (
  question_id: number,
  model?: string,
): Promise<QuestionOut> => {
  const params = new URLSearchParams({ question_id: String(question_id) });
  if (model) params.set("model", model);

  const res = await fetch(`${API_URL}/translate/tard?${params}`, {
    method: "GET",
    // headers:{ "Content-Type": "application/json"},
    credentials: "include",
    // body: JSON.stringify(question)
  });
  if (!res.ok) {
    throw new Error("Erreur lors de la récupération des languages");
  }
  return res.json();
};

export interface TranslationQuestionResult {
  question_id: number;
  status: "success" | "error";
  translations_created?: number;
  translations_count?: number;
  error?: string;
}

export interface TranslationCategoryResponse {
  status: "completed";
  category_id: number;
  model: string;
  count: number;
  processed: number;
  successful_questions: number;
  failed_questions: number;
  translations_created: number;
  results: TranslationQuestionResult[];
}

export interface CategoryTitleTranslationResponse {
  status: "completed";
  category_id: number;
  model: string;
  source_language_code: string;
  source_title: string;
  translations_created: number;
  translations_count: number;
  translations: CategoryTranslationBase[];
}

export const translateCategory = async ({
  categoryId,
  model,
  limit,
}: {
  categoryId: number;
  model: string;
  limit: number;
}): Promise<TranslationCategoryResponse> => {
  const params = new URLSearchParams({
    category_id: String(categoryId),
    model,
    limit: String(limit),
  });

  const res = await fetch(`${API_URL}/translate/tard-category?${params}`, {
    method: "GET",
    credentials: "include",
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(
      typeof errorData.detail === "string"
        ? errorData.detail
        : "Erreur lors de la traduction de la catégorie",
    );
  }

  return res.json();
};

export const translateCategoryTitle = async ({
  categoryId,
  model,
}: {
  categoryId: number;
  model: string;
}): Promise<CategoryTitleTranslationResponse> => {
  const params = new URLSearchParams({
    category_id: String(categoryId),
    model,
  });

  const res = await fetch(`${API_URL}/translate/category-title?${params}`, {
    method: "GET",
    credentials: "include",
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(
      typeof errorData.detail === "string"
        ? errorData.detail
        : "Erreur lors de la traduction du titre de categorie",
    );
  }

  return res.json();
};
import type { NextApiResponse } from "next";

export interface ApiRequestOptions extends RequestInit {
  /** Objet `res` de Next.js à utiliser si tu veux copier les cookies vers le client */
  res?: NextApiResponse;
}
// Generic API request function
async function apiRequest<T>(
  endpoint: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const defaultHeaders: HeadersInit = {
    "Content-Type": "application/json",
  };

  const config: RequestInit = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
    credentials: "include",
  };

  try {
    const response = await fetch(url, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.detail || `HTTP ${response.status}: ${response.statusText}`,
        response.status,
        errorData,
      );
    }
    //  if (options.res) {
    //     const setCookie = response.headers.raw()["set-cookie"] // tableau ou undefined
    //     if (setCookie?.length) {
    //       /**
    //        *  Important :
    //        *  - S'il y a déjà un Set‑Cookie dans la réponse sortante, concatène.
    //        *  - Tu peux aussi filtrer/renommer ici si besoin.
    //        */
    //       options.res.setHeader("Set-Cookie", setCookie)
    //     }
    //   }
    // Handle empty responses
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      return await response.json();
    }

    return {} as T;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${error instanceof Error ? error.message : "Unknown error"}`,
      0,
    );
  }
}

// Authentication API
export const authAPI = {
  async login(credentials: LoginRequest): Promise<Token> {
    const formData = new URLSearchParams();
    formData.append("username", credentials.username);
    formData.append("password", credentials.password);

    const response = await apiRequest<Token>("/auth/login", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: formData,
    });
    console.log(response.access_token);
    // Store token in localStorage
    if (typeof window !== "undefined" && response.access_token) {
      localStorage.setItem("access_token", response.access_token);
    }

    return response;
  },

  async logout(): Promise<void> {
    await apiRequest("/auth/logout", {
      method: "POST",
    });

    // Remove token from localStorage
    if (typeof window !== "undefined") {
      localStorage.removeItem("access_token");
    }
  },

  isAuthenticated(): boolean {
    if (typeof window === "undefined") return false;
    return !!localStorage.getItem("access_token");
  },

  getToken(): string | null {
    if (typeof window === "undefined") return null;
    return localStorage.getItem("access_token");
  },
};

export const languagesApi = {
  async list(): Promise<LanguageOut[]> {
    return apiRequest<LanguageOut[]>("/languages/");
  },
};
// Categories API
export const categoriesAPI = {
  async list(): Promise<CategoryOut[]> {
    return apiRequest<CategoryOut[]>("/categories/admin");
  },

  async create(category: CategoryCreate): Promise<CategoryOut> {
    return apiRequest<CategoryOut>("/categories/", {
      method: "POST",
      body: JSON.stringify(category),
    });
  },
  async remove(id: number): Promise<void> {
    return apiRequest<void>(`/categories/${id}`, {
      method: "DELETE",
    });
  },
  // Assuming an update endpoint exists
  async update(
    id: number,
    category: Partial<CategoryCreate>,
  ): Promise<CategoryOut> {
    return apiRequest<CategoryOut>(`/categories/${id}`, {
      method: "PUT", // or PATCH depending on API
      body: JSON.stringify(category),
    });
  },
  async uploadPhoto(
    id: number,
    file: File,
  ): Promise<{ id: number; url: string }> {
    const formData = new FormData();
    formData.append("file", file);

    const response = await fetch(`${API_BASE_URL}/categories/upload/${id}`, {
      method: "POST",
      credentials: "include",
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.detail || `HTTP ${response.status}: ${response.statusText}`,
        response.status,
        errorData,
      );
    }

    return response.json();
  },
};
// Questions API
export const questionsAPI = {
  async one(id: number): Promise<QuestionOut> {
    return apiRequest<QuestionOut>("/questions/" + id);
  },

  async list(): Promise<QuestionOut[]> {
    return apiRequest<QuestionOut[]>("/questions/");
  },

  async create(question: QuestionCreate): Promise<QuestionOut> {
    return apiRequest<QuestionOut>("/questions/", {
      method: "POST",
      body: JSON.stringify(question),
    });
  },

  uploadImage(questionId: number, file: File): Promise<QuestionOut> {
    return uploadQuestionImage(questionId, file);
  },

  updateImageUrl(questionId: number, imageUrl: string | null): Promise<QuestionOut> {
    return updateQuestionImageUrl(questionId, imageUrl);
  },

  next(questionId: number): Promise<QuestionOut | null> {
    return getNextQuestion(questionId);
  },

  uploadCsv(data: {
    categoryId: number;
    languageCode: string;
    file: File;
  }): Promise<{ status: string; count: number }> {
    return uploadQuestionsCsv(data);
  },

  async ragSimilar(data: {
    category_id: number;
    language_code: string;
    query: string;
    limit?: number;
  }): Promise<RagSimilarQuestion[]> {
    return apiRequest<RagSimilarQuestion[]>("/questions/rag/similar", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  async ragGenerate(data: RagGenerateRequest): Promise<RagGenerateResponse> {
    return apiRequest<RagGenerateResponse>("/questions/rag/generate", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  async ragCreate(data: {
    category_id: number;
    difficulty: string;
    questions: RagGeneratedQuestion[];
  }): Promise<QuestionOut[]> {
    return apiRequest<QuestionOut[]>("/questions/rag/create", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },
};

// Utility functions to transform data between API and UI formats
export const dataTransformers = {
  // Transform API category to UI format
  categoryFromAPI(apiCategory: CategoryOut): any {
    const translations = {
      en: { name: "", description: "" },
      fr: { name: "", description: "" },
      ar: { name: "", description: "" },
      es: { name: "", description: "" },
    };

    // Map API translations to UI format
    apiCategory.translations.forEach((translation) => {
      const langCode = translation.language.code as keyof typeof translations;
      if (translations[langCode]) {
        translations[langCode] = {
          name: translation.name,
          description: translation.name, // API doesn't have description, using name
        };
      }
    });

    return {
      id: apiCategory.id.toString(),
      translations,
      questionCounts: { en: 0, fr: 0, ar: 0, es: 0 }, // Will be calculated separately
      createdAt: new Date(apiCategory.created_at).toISOString().split("T")[0],
      updatedAt: new Date(apiCategory.created_at).toISOString().split("T")[0],
      status: apiCategory.draft ? "draft" : "active",
      createdBy: "admin@example.com",
      tags: [],
    };
  },

  // Transform UI category to API format
  categoryToAPI(uiCategory: any): CategoryCreate {
    const translations: CategoryTranslationBase[] = [];

    Object.entries(uiCategory.translations).forEach(
      ([langCode, translation]: [string, any]) => {
        if (translation.name.trim()) {
          translations.push({
            language: { code: langCode, name: getLanguageName(langCode) },
            name: translation.name,
          });
        }
      },
    );

    return { translations };
  },

  // Transform API question to UI format
  questionFromAPI(apiQuestion: QuestionOut): any {
    // Get the first translation for basic info (assuming single language per question)
    const firstTranslation = apiQuestion.translations[0];

    return {
      id: apiQuestion.id.toString(),
      categoryId: apiQuestion.category_id.toString(),
      question: firstTranslation.question_text,
      options: [
        firstTranslation.option_a,
        firstTranslation.option_b,
        firstTranslation.option_c,
        firstTranslation.option_d,
      ],
      correctAnswer: getCorrectAnswerIndex(firstTranslation.correct_option),
      language: firstTranslation.language.code,
      difficulty: apiQuestion.difficulty,
      imageUrl: apiQuestion.image_url || "",
      createdAt: new Date().toISOString().split("T")[0], // API doesn't provide this
      updatedAt: new Date().toISOString().split("T")[0],
      status: "active",
      tags: [],
      createdBy: "admin@example.com",
      explanation: "",
    };
  },

  // Transform UI question to API format
  questionToAPI(uiQuestion: any): QuestionCreate {
    const correctOption = ["A", "B", "C", "D"][uiQuestion.correctAnswer] || "A";

    const translation: QuestionTranslationBase = {
      language: {
        code: uiQuestion.language,
        name: getLanguageName(uiQuestion.language),
      },
      question_text: uiQuestion.question,
      option_a: uiQuestion.options[0] || "",
      option_b: uiQuestion.options[1] || "",
      option_c: uiQuestion.options[2] || "",
      option_d: uiQuestion.options[3] || "",
      correct_option: correctOption,
    };

    return {
      category_id: Number.parseInt(uiQuestion.categoryId),
      difficulty: uiQuestion.difficulty.toLowerCase(),
      image_url: uiQuestion.imageUrl || null,
      translations: [translation],
    };
  },
};

// Helper functions
function getLanguageName(code: string): string {
  const languageNames: Record<string, string> = {
    en: "English",
    fr: "French",
    ar: "Arabic",
    es: "Spanish",
  };
  return languageNames[code] || code;
}

function getCorrectAnswerIndex(correctOption: string): number {
  const optionMap: Record<string, number> = {
    A: 0,
    B: 1,
    C: 2,
    D: 3,
  };
  return optionMap[correctOption.toUpperCase()] || 0;
}

// Users API
export const usersAPI = {
  async list(): Promise<UserBase[]> {
    return apiRequest<UserBase[]>("/user/list");
  },
  async one(id: number): Promise<UserBase | null> {
    return apiRequest<UserBase | null>(`/user/${id}`);
  },
  async update(id: number, user: UserUpdate): Promise<void> {
    return apiRequest<void>(`/user/${id}`, {
      method: "PUT",
      body: JSON.stringify(user),
    });
  },
  async remove(id: number): Promise<void> {
    return apiRequest<void>(`/user/${id}`, {
      method: "DELETE",
    });
  },
};

export const dashboardAPI = {
  async statistics(): Promise<AdminStatistics> {
    return apiRequest<AdminStatistics>("/user/admin-statistics");
  },
};

export const appVersionAPI = {
  async get(): Promise<AppVersionConfig> {
    return apiRequest<AppVersionConfig>("/app-version/admin");
  },

  async update(config: AppVersionConfig): Promise<AppVersionConfig> {
    return apiRequest<AppVersionConfig>("/app-version/admin", {
      method: "PUT",
      body: JSON.stringify(config),
    });
  },
};

// Export a default API client
export const api = {
  languages: languagesApi,
  auth: authAPI,
  users: usersAPI,
  dashboard: dashboardAPI,
  appVersion: appVersionAPI,
  categories: categoriesAPI,
  questions: questionsAPI,
  transformers: dataTransformers,
};
